import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { useInvitations } from '../hooks/useInvitations';
import { getCatalogDisplayName } from '../utils/stringHelpers';

export const InvitationsBadge: React.FC = () => {
  const { invitations, loading, acceptInvitation, declineInvitation } = useInvitations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccept = async (collectionId: string) => {
    setProcessingInvitation(collectionId);
    try {
      await acceptInvitation(collectionId);
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to accept invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDecline = async (collectionId: string) => {
    setProcessingInvitation(collectionId);
    try {
      await declineInvitation(collectionId);
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to decline invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  if (invitations.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{ color: 'inherit' }}
        aria-label="catalog invitations"
      >
        <Badge badgeContent={invitations.length} color="error">
          <MailOutline />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 320, maxWidth: 400 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="h6">Catalog Invitations</Typography>
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invitations.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No pending invitations
            </Typography>
          </Box>
        ) : (
          invitations.map((invitation, index) => (
            <Box key={invitation.collectionId}>
              {index > 0 && <Divider />}
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {getCatalogDisplayName(invitation.collectionName)}
                </Typography>
                {invitation.collectionDescription && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {invitation.collectionDescription}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  Role: {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Invited by: {invitation.invitedByName || 'Unknown'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleAccept(invitation.collectionId)}
                    disabled={processingInvitation === invitation.collectionId}
                  >
                    Accept
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDecline(invitation.collectionId)}
                    disabled={processingInvitation === invitation.collectionId}
                  >
                    Decline
                  </Button>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Menu>
    </>
  );
};
